// Replaces the React/redux facility finder. Same behaviour: search on submit,
// show three results at a time, Load More reveals another page, Close collapses
// back to the first page.
//
// The cookies the redux version wrote are not reproduced. They existed to
// restore a previous search on load, and the only mount passes no searchterm,
// so nothing ever read them back.
import { Controller } from '@hotwired/stimulus';

const MESSAGES = {
  empty: 'No results to show',
  failed: "Oops. We're having difficulty with that one. Please try again",
  loading: 'Finding results.'
};

export default class extends Controller {
  static targets = ['actions', 'input', 'message', 'results'];

  static values = {
    activityTypeIds: Array,
    createUrl: String,
    pagination: { type: Number, default: 3 },
    searchType: { type: String, default: 'activity' }
  };

  initialize() {
    this.items = [];
    this.page = 1;
  }

  close(event) {
    event.preventDefault();
    this.page = 1;
    this.render();
  }

  loadMore(event) {
    event.preventDefault();
    this.page += 1;
    this.render();
  }

  async search(event) {
    event.preventDefault();
    if (this.searchterm === '') return;

    this.page = 1;
    this.showMessage(MESSAGES.loading);
    try {
      this.items = await this.fetchResults();
      this.render();
    } catch (error) {
      this.items = [];
      this.render(MESSAGES.failed);
    }
  }

  async fetchResults() {
    const response = await fetch(this.createUrlValue, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ venue_search: this.searchPayload() })
    });
    if (!response.ok) throw new Error(`Search failed with ${response.status}`);
    return response.json();
  }

  searchPayload() {
    const payload = { searchterm: this.searchterm };
    const key = this.searchTypeValue === 'activity' ? 'activity_type_ids' : 'facility_type_ids';
    payload[key] = this.activityTypeIdsValue;
    return payload;
  }

  get searchterm() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : '';
  }

  get visible() {
    return this.paginationValue * this.page;
  }

  render(message) {
    this.showMessage(message || (this.items.length === 0 ? MESSAGES.empty : ''));
    this.resultsTarget.innerHTML = this.items
      .slice(0, this.visible)
      .map(venue => this.resultMarkup(venue))
      .join('');
    this.renderActions();
  }

  renderActions() {
    this.actionsTarget.hidden = this.items.length <= this.visible;
  }

  showMessage(message) {
    this.messageTarget.textContent = message;
    this.messageTarget.hidden = message === '';
  }

  resultMarkup(venue) {
    return `
      <article class="facility-finder__result">
        <div class="facility-finder__column facility-finder__column--title">
          <h1 class="gg facility-finder__result-heading">
            <a href="${venue.path}" class="facility-finder__result-link">${venue.title}</a>
          </h1>
          <a href="${venue.activity_path}" class="facility-finder__result-activities">See all activities</a>
        </div>
        <div class="facility-finder__column facility-finder__column--hide-mobile">
          <div class="facility-finder__address">${venue.address}</div>
        </div>
        <div class="facility-finder__column facility-finder__column--cta">
          <a class="call-to-action call-to-action--primary call-to-action--join" href="${venue.join_path}">Join now</a>
        </div>
      </article>
    `;
  }
}
